import React, { useRef, useMemo } from 'react';
import MaterialTable from 'material-table';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';

const TagTable = ({ can_create_tag, can_delete_tag }) => {
  const tableRef = useRef();

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: { main: '#7cb342' },
          secondary: { main: '#4caf50' },
        },
      }),
    []
  );

  const columns = useMemo(
    () => [
      { title: 'Name', field: 'attributes.name' },
      { title: 'Created at', field: 'attributes.created_at', editable: 'never' },
      { title: 'Tagging count', field: 'attributes.taggings_count', editable: 'never' },
    ],
    []
  );

  const getCsrfToken = () => {
    const tokenElement = document.querySelector('[name=csrf-token]');
    return tokenElement ? tokenElement.content : '';
  };

  const fetchWithCsrf = async (url, options = {}) => {
    const csrfToken = getCsrfToken();
    const headers = {
      'Content-Type': 'application/json; charset=UTF-8',
      'X-CSRF-Token': csrfToken,
      ...(options.headers || {}),
    };

    const response = await fetch(url, { ...options, headers });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Request failed: ${response.status} - ${errorText}`);
    }
    return response.json();
  };

  const fetchData = async (query) => {
    try {
      const url = new URL('/api/tags', window.location.origin);
      url.searchParams.append('per_page', query.pageSize);
      url.searchParams.append('q', query.search || '');
      url.searchParams.append('page', query.page + 1); // API uses 1-based page numbering

      // Handle sorting
      if (query.orderBy) {
        // Extract the field name from query.orderBy
        const orderField = query.orderBy.field; // query.orderBy is an object, we need the 'field' value
        const orderDirection = query.orderDirection || 'asc'; // Default to ascending order if not provided

        // Map the orderBy field to match the backend field names
        let backendField = orderField;
        if (orderField === 'attributes.name') {
          backendField = 'name'; // Match the backend field name for 'name'
        } else if (orderField === 'attributes.created_at') {
          backendField = 'created_at'; // Match the backend field name for 'created_at'
        } else if (orderField === 'attributes.taggings_count') {
          backendField = 'taggings_count'; // Match the backend field name for 'taggings_count'
        }

        // Append sorting parameters to the URL
        url.searchParams.append('sort_by', backendField);
        url.searchParams.append('sort_order', orderDirection);
      }

      const response = await fetchWithCsrf(url.toString());
      const { data, pagination } = response;

      return {
        data: data || [],
        page: (pagination.page ?? 1) - 1, // Convert to zero-based page
        totalCount: pagination.count ?? 0, // Total items count
      };
    } catch (error) {
      console.error('Error fetching tags:', error);
      return {
        data: [],
        page: 0,
        totalCount: 0,
      };
    }
  };

  const handleAdd = async (newData) => {
    await fetchWithCsrf('/api/tags/', {
      method: 'POST',
      body: JSON.stringify(newData),
    });
    if (tableRef.current) {
      tableRef.current.onQueryChange();
    }
  };

  const handleUpdate = async (newData) => {
    await fetchWithCsrf(`/api/tags/${newData.id}`, {
      method: 'PUT',
      body: JSON.stringify(newData),
    });
    if (tableRef.current) {
      tableRef.current.onQueryChange();
    }
  };

  const handleDelete = async (oldData) => {
    await fetchWithCsrf(`/api/tags/${oldData.id}`, {
      method: 'DELETE',
    });
    if (tableRef.current) {
      tableRef.current.onQueryChange();
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <MaterialTable
        title="Tag Management"
        columns={columns}
        tableRef={tableRef}
        actions={[
          {
            icon: 'refresh',
            tooltip: 'Refresh Data',
            isFreeAction: true,
            onClick: () => tableRef.current && tableRef.current.onQueryChange(),
          }
        ]}
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 20, 30, 50],
          exportButton: true,
          sorting: true,
          search: true,
          // selection: true,
          addRowPosition: 'first',
        }}
        onRowClick={(event, rowData) => Turbolinks.visit(rowData.attributes.url)}
        data={(query) => fetchData(query)}
        editable={
          can_create_tag
            ? {
                onRowAdd: (newData) =>
                  new Promise((resolve, reject) => {
                    handleAdd(newData).then(resolve).catch((err) => {
                      console.error(err);
                      reject(err);
                    });
                  }),
                onRowUpdate: (newData) =>
                  new Promise((resolve, reject) => {
                    handleUpdate(newData).then(resolve).catch((err) => {
                      console.error(err);
                      reject(err);
                    });
                  }),
                onRowDelete: can_delete_tag
                  ? (oldData) =>
                      new Promise((resolve, reject) => {
                        handleDelete(oldData).then(resolve).catch((err) => {
                          console.error(err);
                          reject(err);
                        });
                      })
                  : undefined,
              }
            : undefined
        }
      />
    </ThemeProvider>
  );
};

export default TagTable;
